/**
 * @summary this file deals with everything around click portal schools. From requesting schools to managing their impression tracking
 */
import {
  CLICK_PORTAL_SEARCH_FILTERS,
  QUERY_PARAMS,
  SITE_TYPE,
  SCHOOL_IMPRESSION_TYPES,
} from 'consts';
import { triadSuperFetch } from 'utils/superFetch';
import isBrowser from 'utils/isBrowser';
import { wait } from 'utils/generalUtils';
import { getUserSessionId } from 'utils/analyticsHelpers';
import { getFormSelectionObjectsFromQuery } from 'components/form-wizards/click-portal-search/utils/getFormSelectionObjects';
import { getImageUrlMap } from 'utils/imageHelpers';
import promiseDebounce from 'utils/promiseDebounce';
import { APIError } from 'utils/errors/APIError';
import { APITransformError } from 'utils/errors/APITransformError';
import { getTriadBackendAPIDomain } from 'configs';
import type {
  SuperFetchReturn,
  GetSchoolListingRawResponse,
  GetMultiRowSchoolListingRawResponse,
  GetSchoolListingResults,
  MultiRowSchoolListingResults,
  SchoolFilters,
  TransformedTaxonomyNode,
} from 'types';
import { ErrorPriority } from 'types';
import { LogError } from 'utils/logging/LogError';
import { LogInfo, LogWarning } from 'utils/logging';
import { TRIAD_PROXY_ROUTE } from './apiConstants';

let didTrackImpression = false;
let didInitImpressionDebugging = false;
export function initImpressionDebugging(): void {
  if (didInitImpressionDebugging) {
    return;
  }
  didInitImpressionDebugging = true;
  window.addEventListener('beforeunload', () => {
    if (!didTrackImpression) {
      LogWarning('No Impressions Tracked On Listing Page');
    } else {
      LogInfo('Some Impressions Tracked On Listing Page');
    }
  });
}

interface RecordViewPayload {
  method: string;
  url: string;
  body: {
    viewLocation: string;
    schoolImpressionGuid: string;
    schoolDegreeInfoGuids: string[];
    schoolCode: string;
  };
}

const { DEGREES, CONCENTRATIONS, SUBJECTS, FILTER_KEY } =
  CLICK_PORTAL_SEARCH_FILTERS;

const { CP_REQUEST_IMPRESSIONS, CP_REQUEST_IMPRESSIONS_CACHE_KEY } =
  SCHOOL_IMPRESSION_TYPES;

export const schoolImpressionPayloadQueue: Record<string, RecordViewPayload> =
  {};

/**
 * @summary this will get the click portal school results cache key
 */
export function generateSchoolListingCacheKey(
  filtersArray: SchoolFilters[]
): string | null {
  if (filtersArray.length !== 1) {
    return null;
  }
  // TODO: handle multi row in the future, for now only support single row
  const filters = filtersArray[0];

  Object.keys(filters).forEach((key) => {
    const _key = key as CLICK_PORTAL_SEARCH_FILTERS;
    if (![DEGREES, CONCENTRATIONS, SUBJECTS, FILTER_KEY].includes(_key)) {
      LogWarning(
        'We have added an Unknown filter that needs to be present in the cache key'
      );
    }
  });

  const degreeValue =
    filters[DEGREES] && typeof filters[DEGREES] !== 'string'
      ? filters[DEGREES]?.value
      : null;

  const subjectValue =
    filters[SUBJECTS] && typeof filters[SUBJECTS] !== 'string'
      ? filters[SUBJECTS]?.value
      : null;

  const concentrationValue =
    filters[CONCENTRATIONS] && typeof filters[CONCENTRATIONS] !== 'string'
      ? filters[CONCENTRATIONS]?.value
      : null;

  const filterKey = filters[FILTER_KEY];

  return [degreeValue, subjectValue, concentrationValue, filterKey]
    .map((value) => value || 'undefined')
    .join('|');
}

/**
 * @summary its possible we don't have the impression key at the time we render the  schools. In this case queue up the impressions
 * @param {Object} programPayload - see trackClickPortalImpression on the payload we send to backend
 * @private
 */
function _addToImpressionQueue(
  schoolGuid: string,
  programPayload: RecordViewPayload
): void {
  // LogInfo('Log Impression: adding to impression queue', {
  //   description: `schoolGuid = ${schoolGuid}`,
  // });
  if (schoolGuid && programPayload.body) {
    schoolImpressionPayloadQueue[schoolGuid] = programPayload;
  } else {
    LogError('Log Impression: failed to add to impression queue', {
      priority: ErrorPriority.THREE,
      description: `schoolGuid = ${schoolGuid}`,
    });
  }
}

const schoolImpressionBatch = new Set<
  Omit<RecordViewPayload, 'schoolDegreeInfoGuids'>
>();

function _processBatchSchoolImpression(
  schoolCode: string
): Promise<SuperFetchReturn<unknown>[]> {
  const payloads = Array.from(schoolImpressionBatch);
  schoolImpressionBatch.clear();
  const locations: Record<string, string[]> = {};
  payloads.forEach(({ body: { viewLocation, schoolImpressionGuid } }) => {
    locations[viewLocation] = locations[viewLocation] || [];
    locations[viewLocation].push(schoolImpressionGuid);
  });

  LogInfo('Log Impression: Sending RecordView batch');

  return Promise.all(
    Object.keys(locations).map((location) => {
      return triadSuperFetch(`${TRIAD_PROXY_ROUTE}/ClickPortal/RecordView`, {
        method: 'POST',
        timeout: 3000,
        body: {
          schoolCode,
          viewLocation: location,
          schoolImpressionGuids: locations[location],
        },
        retry: {
          maxAttempts: 3,
          waitTimeBetweenFails: 1000,
        },
      }).then((response) => {
        didTrackImpression = true;
        LogInfo('Log Impression: RecordView sent');
        return response;
      });
    })
  );
}

const _debouncedSchoolImpressionBatch = promiseDebounce(
  _processBatchSchoolImpression,
  600
) as (schoolCode: string) => Promise<SuperFetchReturn<unknown>[]>;

function _batchSchoolImpression(
  payload: RecordViewPayload
): Promise<SuperFetchReturn<unknown> | SuperFetchReturn<unknown>[] | void> {
  if (!payload.body.schoolImpressionGuid) {
    LogError('School impression guid is required', {
      description: `schoolGuid = ${payload.body.schoolCode}`,
      priority: ErrorPriority.ONE,
    });
    return Promise.resolve();
  }

  if (payload.body.schoolDegreeInfoGuids?.length > 0) {
    LogInfo('Log Impression: RecordProgramView sent');
    return triadSuperFetch(
      `${TRIAD_PROXY_ROUTE}/ClickPortal/RecordProgramView`,
      {
        method: 'POST',
        timeout: 3000,
        body: {
          viewLocation: payload.body.viewLocation,
          schoolImpressionGuid: payload.body.schoolImpressionGuid,
          schoolDegreeInfoGuids: payload.body.schoolDegreeInfoGuids,
          schoolCode: payload.body.schoolCode,
        },
      }
    );
  }

  schoolImpressionBatch.add(payload);
  LogInfo('Log Impression: School impression batch added', {
    description: `schoolImpressionGuid = ${payload.body.schoolImpressionGuid}`,
  });
  return _debouncedSchoolImpressionBatch(payload.body.schoolCode);
}

/**
 * @summary its possible that some impressions were queued up before the schools were rendered. In this case send them now.
 */
function _processImpressionQueue(
  schools: GetSchoolListingResults['results'] = []
): void {
  // LogInfo('Log Impression: Processing impression queue');
  try {
    schools.forEach((school) => {
      const payload = schoolImpressionPayloadQueue[school.id];

      if (payload) {
        const _payload = {
          ...payload,
          body: {
            ...payload.body,
            schoolImpressionGuid: school.impressionGuid || '',
          },
        };

        _batchSchoolImpression(_payload).catch((error: Error) => {
          LogError('Log Impression: Failed to process impression queue', {
            description: error.message,
            priority: ErrorPriority.TWO,
          });
        });
      }

      delete schoolImpressionPayloadQueue[school.id];
    });
  } catch (error: unknown) {
    LogError(
      `Log Impression: Failed to process impression Queue ${
        (error as Error).message
      }`,
      {
        description: (error as Error).message,
        priority: ErrorPriority.TWO,
      }
    );
  }
}

/**
 * @summary Determines if impressions should be requested based on portal type and session information
 */
export function shouldRequestImpressions(
  isOnBrowser: boolean,
  sessionId: string
): boolean {
  if (!isOnBrowser) {
    return false;
  }

  if (sessionId) {
    return true;
  }

  return false;
}

function getFilterValue(
  filter: SchoolFilters[CLICK_PORTAL_SEARCH_FILTERS]
): string | null {
  return typeof filter === 'string' ? filter : filter?.value || null;
}

function getFilterBody(
  schoolFilters: SchoolFilters[],
  isMultiRow: boolean,
  isCurated: boolean
): Record<string, unknown> {
  // Map over the filters array to create the request body structure

  const mappedFilters = schoolFilters.map((filter) => ({
    FilterKey: getFilterValue(filter[FILTER_KEY]),
    DegreeType: getFilterValue(filter[DEGREES]),
    ParentCategory: getFilterValue(filter[SUBJECTS]),
    Category: getFilterValue(filter[CONCENTRATIONS]),
  }));

  if (isMultiRow) {
    // BE will reject a non-curated request that has multiple filters, so we need to send only the first filter
    return {
      requestRows: isCurated ? mappedFilters : [mappedFilters[0]],
    };
  }

  return mappedFilters[0];
}

/**
 * @summary this will get the click portal school results
 */
async function _getSchoolListings(
  schoolFilters: SchoolFilters[],
  siteType: string,
  sessionId: string,
  meta: {
    isPersonalized: boolean;
    geoLocation: Record<string, unknown>;
    originalUrl: string;
    queryParams?: Record<string, unknown>;
    schoolCode: string;
    cookies: Record<string, unknown>;
    isMultiRow: boolean;
    isCurated: boolean;
  }
): Promise<MultiRowSchoolListingResults> {
  const {
    isPersonalized = false,
    geoLocation = {},
    originalUrl = '',
    queryParams,
    schoolCode,
    cookies,
    isMultiRow = false,
    isCurated = false,
  } = meta || {};
  const path = isBrowser()
    ? TRIAD_PROXY_ROUTE
    : getTriadBackendAPIDomain(cookies);
  const isMicroPortal = siteType === SITE_TYPE.MICRO_PORTAL;

  function getRequestType(): string | null {
    if (isMultiRow) {
      return 'MultiClickPortal';
    }

    if (isMicroPortal) {
      return 'MicroClickportal';
    }
    return null;
  }

  let endpointUrl = `${path}/ClickPortal/GetSchoolListings`;
  if (isMultiRow) {
    endpointUrl = `${path}/ClickPortal/GetMultiSchoolListings`;
  } else if (isMicroPortal) {
    endpointUrl = `${path}/microportal/GetMPSchoolListings`;
  }

  // Note this will only be cached on browser
  const cacheKey = isBrowser()
    ? generateSchoolListingCacheKey(schoolFilters)
    : null;

  const _shouldRequestImpressions = shouldRequestImpressions(
    isBrowser(),
    isBrowser() ? getUserSessionId() : null
  );

  // Check if the filterKey contains '&' and log an error
  // error logs are showing filterKeys being passed that are concatenated with other params
  const filterKey = schoolFilters[0][FILTER_KEY];

  if (
    filterKey &&
    typeof filterKey === 'string' &&
    (filterKey.includes('&') || filterKey.includes('&amp;'))
  ) {
    LogError('Invalid FilterKey: contains the & character', {
      priority: ErrorPriority.TWO,
      description: `filterKey: ${filterKey}`,
    });
  }

  let impressionType;

  if (_shouldRequestImpressions) {
    impressionType = CP_REQUEST_IMPRESSIONS;
  } else {
    impressionType = CP_REQUEST_IMPRESSIONS_CACHE_KEY;
  }

  let _queryParams: { name: string; value: unknown }[] | undefined;

  // on micro portal we will get these server side. We don't cache this page so its save
  // on the browser this happens in the base request function for all requests to triad
  if (queryParams && Object.keys(queryParams).length > 0) {
    _queryParams = Object.keys(queryParams).map((key) => ({
      name: key,
      value: Array.isArray(queryParams[key])
        ? queryParams[key].join(',')
        : queryParams[key],
    }));
  }

  const [apiError, response, metadata] = await triadSuperFetch<
    GetSchoolListingRawResponse | GetMultiRowSchoolListingRawResponse
  >(endpointUrl, {
    method: 'POST',
    timeout: 12000, // 12 seconds
    body: {
      impressionType,
      isCurated,
      sessionId,
      includeGeoLocation: isPersonalized, // when called from browser this will tell server to include geoLocation
      // See [...endpoints].js for how this gets added from browser
      geoLocation: isBrowser() ? {} : geoLocation, // when called from server we pass it in directly,
      originalUrl,
      requestType: getRequestType(),
      queryParams: _queryParams,
      schoolCode,
      ...getFilterBody(schoolFilters, isMultiRow, isCurated),
    },
    cache: {
      type: 'sessionStorage',
      key: cacheKey,
    },
    retry: {
      maxAttempts: 3,
      waitTimeBetweenFails: 1000,
    },
  });

  if (apiError) {
    throw apiError;
  }

  let schoolListingsResponse;
  // getMultiSchoolListings returns an array of results
  if ('results' in response) {
    schoolListingsResponse = response.results;
  } else {
    // getSchoolListings returns a single result
    schoolListingsResponse = [response];
  }

  // check if all results have errors
  const isValidResponse = schoolListingsResponse.every(
    (listing) => !listing?.Errors?.length
  );

  let errorMessage = '';
  if (!isValidResponse) {
    errorMessage = schoolListingsResponse
      .map((result) => result.Errors || [])
      .flat()
      .join('; ');

    // We don't want to throw an error here because it will cause the page to not render if one of the rows has an error
    LogError('_getSchoolListings: Error in one or more rows returned', {
      priority: ErrorPriority.ONE,
      schoolCode,
      description:
        errorMessage ||
        'Get School Listing API Failed but no backend error returned',
    });
  }

  // Filter out rows with errors to prevent processing invalid data
  const validResponses = schoolListingsResponse.filter(
    (singleResponse) => singleResponse?.Listings?.length > 0
  );

  if (validResponses.length === 0) {
    LogError('_getSchoolListings: No valid responses returned', {
      priority: ErrorPriority.ONE,
      schoolCode,
      description: errorMessage || 'No valid responses returned',
    });
  }

  try {
    if (metadata.isFromCache) {
      await wait(500);
    }
    const resultRows = validResponses.map((singleResponse) => {
      const results = singleResponse.Listings.map((school) => ({
        id: school.value,
        name: school.label,
        schoolLogoUrl: getImageUrlMap(school.schoolImages),
        featuredImage:
          getImageUrlMap(
            school.featuredImage,
            'https://cms-prd.triadplatform.com/wp-content/uploads/sites/17/2024/10/counseling_05.jpg'
          ) || null,
        description:
          school.schoolDesc?.[0] ||
          'Description not available please check back soon.',

        rating: school.rating || null,
        url: school.destinationUrl || null,
        learningType: school.learningEnvironment || null,
        isOffsiteConversion: school.IsOffsiteConversion || false,
        highlights: [school.highlights || ''],
        selectedProgram: school.selectedProgram || {},
        otherPrograms: school.programs || [],
        filteredProgramCount: school.filteredProgramCount || null,
        programCount: school.programCount || null,
        matchingCategory: school.matchingCategory || null,
        schoolCode: school.schoolCode || null,
        revenue: school.Revenue || 0,
        // If not from cache and there is an impressionGuid set to GUID else null out
        impressionGuid: metadata.isFromCache
          ? null
          : school.impressionGuid || '',
        filterTagName: school.FilterTagName || '',
      }));

      const hasImpressionGuids =
        !metadata.isFromCache && _shouldRequestImpressions;

      if (!hasImpressionGuids && !singleResponse.ImpressionKey) {
        LogError(
          'School results are loaded but their are both no impression guids and we have no cache key to fetch them.',
          {
            priority: ErrorPriority.TWO,
            description: `hasImpressionGuids: ${hasImpressionGuids}, singleResponse.ImpressionKey: ${singleResponse.ImpressionKey}`,
          }
        );
      }

      return {
        hasImpressionGuids,
        title: singleResponse.Headline1 || '',
        subTitle: singleResponse.Headline2 || '',
        impressionCacheKey: singleResponse.ImpressionKey || '',
        schoolImpressionGroupGuid: singleResponse.ImpressionKey || '',
        results,
        hasTags: singleResponse.HasTags || false,
        filterKeyDerivedValues: {
          hasDerivedValues: Boolean(
            singleResponse.ParentCategory &&
              singleResponse.DegreeType &&
              singleResponse.Category
          ),
          parent_category_guid: singleResponse.ParentCategory || null,
          degree_level_guid: singleResponse.DegreeType || null,
          category_guid: singleResponse.Category || null,
        },
      };
    });

    return {
      resultRows,
      isMultiRow,
      firstRow: resultRows[0] || null,
    };
  } catch (error) {
    throw new APITransformError((error as Error).message, 'getSchoolListings');
  }
}

interface GetSchoolImpressionsResponse {
  SchoolImpressions: {
    [impressionCacheKey: string]: {
      [schoolGuid: string]: {
        SchoolGuid: string;
        value: string;
        label: string;
        CreateDate: string;
        SchoolImpressionTypeId: number;
        SchoolImpressionSourceId: number;
      };
    };
  };
}

/**
 * @summary this function will request impression GUIDs from the backend and attach them to the school listings results
 */

export async function handleImpressionKeyMapping(
  schoolListingResults: GetSchoolListingResults | MultiRowSchoolListingResults,
  schoolCode: string
): Promise<MultiRowSchoolListingResults> {
  // Convert single row case to multi-row format
  const rows =
    'resultRows' in schoolListingResults
      ? schoolListingResults.resultRows
      : [schoolListingResults];

  const impressionKeys = rows
    .map((row) => row.impressionCacheKey)
    .filter((key): key is string => Boolean(key));

  if (impressionKeys.length === 0) {
    LogWarning('Log Impression: No Impression Keys returned from backend');
    return {
      resultRows: rows,
      isMultiRow: 'resultRows' in schoolListingResults,
      firstRow: rows[0],
    };
  }

  // Always use the multi endpoint
  const [apiError, response] =
    await triadSuperFetch<GetSchoolImpressionsResponse>(
      `${TRIAD_PROXY_ROUTE}/ClickPortal/GetMultiSchoolImpressions`,
      {
        method: 'POST',
        timeout: 3000,
        body: { impressionKeys, schoolCode },
        retry: {
          maxAttempts: 3,
          waitTimeBetweenFails: 1000,
        },
      }
    );

  if (apiError) {
    LogError(
      'Log Impression: Failed to map school impression guids back to results',
      {
        description: apiError.message,
        priority: ErrorPriority.TWO,
      }
    );
    return {
      resultRows: rows,
      isMultiRow: 'resultRows' in schoolListingResults,
      firstRow: rows[0],
    };
  }

  try {
    const { SchoolImpressions } = response;

    const updatedRows = rows.map((row) => {
      if (!row.impressionCacheKey) {
        return row;
      }

      const rowImpressions = SchoolImpressions[row.impressionCacheKey] || {};
      const mappedResults = row.results.map((result) => {
        const impression = rowImpressions[result.id];
        return {
          ...result,
          impressionGuid: impression?.value ?? null,
        };
      });

      _processImpressionQueue(mappedResults);
      return { ...row, results: mappedResults };
    });

    return {
      resultRows: updatedRows,
      isMultiRow: 'resultRows' in schoolListingResults,
      firstRow: updatedRows[0],
    };
  } catch (error) {
    LogError(
      'Log Impression: Failed to map school impression guids back to results',
      {
        description: (error as Error).message,
        priority: ErrorPriority.TWO,
      }
    );
    return {
      resultRows: rows,
      isMultiRow: 'resultRows' in schoolListingResults,
      firstRow: rows[0],
    };
  }
}

/**
 * @deprecated
 * @summary this function will track and impression on the backend
 */
export function trackClickPortalImpression({
  schoolId,
  impressionGuid,
  programGuids,
  viewLocation,
  schoolCode,
}: {
  schoolId: string;
  impressionGuid: string;
  programGuids: string[];
  viewLocation: string;
  schoolCode: string;
}): void {
  // IF CHANGING THIS, CHANGE processQueue in schoolImpressionQueue
  const payload = {
    method: '',
    url: '',
    body: {
      viewLocation,
      schoolImpressionGuid: impressionGuid,
      schoolDegreeInfoGuids: programGuids,
      schoolCode,
    },
  };

  if (!impressionGuid) {
    LogInfo(
      'Log Impression: No impression guid found, adding to impression queue',
      {
        description: `schoolId = ${schoolId}`,
      }
    );
    _addToImpressionQueue(schoolId, payload);
    return;
  }

  LogInfo('Log Impression: Impression guid found, adding to impression queue', {
    description: `schoolId = ${schoolId}`,
  });
  _batchSchoolImpression(payload).catch((error: Error) => {
    LogError('Log Impression: Failed to process impression queue', {
      description: error.message,
      priority: ErrorPriority.TWO,
    });
  });
}

/**
 * @summary get school listing from query
 */
export default async function schoolListingsResultsHandler(
  filters: SchoolFilters[],
  siteType: string,
  sessionId: string,
  meta: {
    isPersonalized: boolean;
    geoLocation: Record<string, unknown>;
    originalUrl: string;
    queryParams: Record<string, unknown>;
    schoolCode: string;
    cookies: Record<string, unknown>;
    isMultiRow: boolean;
    isCurated: boolean;
  }
): Promise<
  MultiRowSchoolListingResults & {
    error: string;
    message: string;
    isLoading: boolean;
  }
> {
  const {
    isPersonalized = false,
    geoLocation = {},
    originalUrl = '',
    queryParams = {},
    schoolCode,
    cookies,
    isMultiRow,
    isCurated = false,
  } = meta || {};

  const schoolResults = await _getSchoolListings(
    filters,
    siteType,
    sessionId,
    /* meta */ {
      isPersonalized,
      geoLocation,
      originalUrl,
      queryParams,
      schoolCode,
      cookies,
      isMultiRow,
      isCurated,
    }
  );

  return {
    ...schoolResults,
    message: '',
    error: '',
    isLoading: false,
  };
}

/**
 * @summary Consolidated function to handle school listing requests with impression mapping and state updates
 */
export async function handleSchoolResultsRequestWithUpdates(
  filters: SchoolFilters[],
  siteType: string,
  sessionId: string,
  meta: {
    isPersonalized: boolean;
    geoLocation: Record<string, unknown>;
    originalUrl: string;
    queryParams?: Record<string, unknown>;
    schoolCode: string;
    cookies: Record<string, unknown>;
    isMultiRow: boolean;
    isCurated?: boolean;
  },
  updateHandler: (
    data: MultiRowSchoolListingResults & { isLoading?: boolean }
  ) => void
): Promise<MultiRowSchoolListingResults> {
  updateHandler({ isLoading: true } as MultiRowSchoolListingResults & {
    isLoading: boolean;
  });

  const schoolListingResults = await schoolListingsResultsHandler(
    filters,
    siteType,
    sessionId,
    {
      isPersonalized: meta.isPersonalized,
      geoLocation: meta.geoLocation,
      originalUrl: meta.originalUrl,
      queryParams: meta.queryParams || {},
      schoolCode: meta.schoolCode,
      cookies: meta.cookies,
      isMultiRow: meta.isMultiRow,
      isCurated: meta.isCurated || false,
    }
  );

  updateHandler(schoolListingResults);

  // If no impression guids then get new impression guids
  if (!schoolListingResults?.firstRow?.hasImpressionGuids) {
    LogInfo('Listing Results: No impression guids found, requesting new ones');
    handleImpressionKeyMapping(schoolListingResults, meta.schoolCode).then(
      (updatedResults) => {
        updateHandler(updatedResults);
      }
    );
  } else {
    LogInfo('Listing Results: Impression guids found, skipping request');
  }

  return schoolListingResults;
}

/**
 * @summary get school listing from query
 */
export async function getSchoolListingResultsFromQuery(
  query: Record<string, string>,
  programTaxonomyMap: {
    degrees: TransformedTaxonomyNode[];
  },
  meta: {
    isPersonalized: boolean;
    geoLocation: Record<string, unknown>;
    originalUrl: string;
    queryParams: Record<string, unknown>;
    sessionId: string;
    siteType: string;
    schoolCode: string;
    cookies: Record<string, unknown>;
    isMultiRow: boolean;
    isCurated: boolean;
  }
): Promise<
  MultiRowSchoolListingResults & { currentSelection?: Record<string, unknown> }
> {
  const {
    isPersonalized,
    geoLocation,
    sessionId,
    siteType,
    originalUrl,
    queryParams,
    schoolCode,
    cookies,
    isMultiRow,
    isCurated = false,
  } = meta || {};
  if (!sessionId && siteType === 'microPortal') {
    LogError('no sessionId on Microportal', {
      priority: ErrorPriority.ONE,
      description: `siteType: ${siteType}`,
    });
  }

  try {
    const filters = {
      [FILTER_KEY]: query[QUERY_PARAMS.FILTER_KEY_PARAM],
      ...getFormSelectionObjectsFromQuery(query, programTaxonomyMap),
    };
    const results = await schoolListingsResultsHandler(
      [filters],
      siteType,
      sessionId,
      /* meta */ {
        isPersonalized,
        geoLocation,
        originalUrl,
        queryParams,
        schoolCode,
        cookies,
        isMultiRow,
        isCurated,
      }
    );

    const hasFormQueryParams =
      query[QUERY_PARAMS.PARENT_CAT_GUID_PARAM] &&
      query[QUERY_PARAMS.DEGREE_GUID_PARAM] &&
      query[QUERY_PARAMS.CATEGORY_GUID_PARAM];
    let currentSelection;

    // Its possible on the server we get form values to select from API
    // This is also done on the browser in the GlobalReducer in case of client side routing
    // TODO: [T1-9137] lets consolidate this logic and remove it from the reducer.
    //       When getting and setting the results on the browser lets
    //       just set the current selection then as well
    if (results?.firstRow?.filterKeyDerivedValues?.hasDerivedValues) {
      currentSelection = getFormSelectionObjectsFromQuery(
        results.firstRow.filterKeyDerivedValues,
        programTaxonomyMap
      );
    } else if (hasFormQueryParams) {
      currentSelection = getFormSelectionObjectsFromQuery(
        query,
        programTaxonomyMap
      );
    }
    // it's possible that the backend sends a default result set back if the selection was invalid
    // so unless all three values are still populated, assume there's no currentSelection

    if (
      currentSelection &&
      Object.values(currentSelection).every((selection) => selection?.value)
    ) {
      return {
        ...results,
        currentSelection,
      };
    }

    return results;
  } catch (error: unknown) {
    if (error instanceof APIError) {
      throw error;
    }

    if (error instanceof APITransformError) {
      throw error;
    }

    throw new APITransformError(
      (error as Error).message,
      'getSchoolListingResultsFromQuery'
    );
  }
}
